import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Box,
  Wrench,
  ShieldAlert,
  MapPin,
  Activity,
  AlertTriangle,
  Clock,
  Layers,
  Cpu,
  Radio,
  FolderTree,
  FileText,
  FileSpreadsheet,
  FileCode2,
  Image as ImageIcon,
  File as FileGenericIcon,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from "recharts";
import { KPICard } from "../components/ui/KPICard";
import { Card, CardHeader, CardTitle } from "../components/ui/Card";
import { StatusBadge } from "../components/ui/StatusBadge";
import { SkeletonCard } from "../components/ui/LoadingSpinner";
import api from "../lib/api";
import { useAppStore } from "../stores/appStore";
import { useSiteSelection } from "../components/layout/OrgSiteSelector";
import { formatDate } from "../lib/utils";
import {
  type MaintenanceTask,
  fetchAllSiteMaintenanceTasks,
} from "../lib/maintenanceApi";
import {
  listDevices as listInstrumentationDevices,
  listSensors as listInstrumentationSensors,
} from "../lib/instrumentationApi";
import {
  listDocuments as listDMSDocuments,
  listFolders as listDMSFolders,
  type DMSDocumentType,
} from "../lib/dmsApi";

interface DashboardData {
  totalAssets: number;
  totalLocations: number;
  totalPointClouds: number;
  totalDigitalTwins: number;
  totalDevices: number;
  totalSensors: number;
  activeSensors: number;
  totalDocuments: number;
  totalFolders: number;
  documentTypeBreakdown: Array<{ type: DMSDocumentType; count: number }>;
  overdueMaintenance: number;
  pendingMaintenance: number;
  averageRiskScore: number;
  highRiskCount: number;
  recentDevices: Array<{
    id: string;
    name: string;
    status: string;
    device_type: string | null;
    serial_number: string | null;
    sensor_count: number;
    updated_at: string;
  }>;
  recentAssets: Array<{
    id: string;
    name: string;
    status: string;
    created_at: string;
  }>;
  recentMaintenance: Array<{
    id: string;
    description: string;
    status: string;
    scheduled_date: string;
  }>;
  assetsByStatus: Array<{ name: string; value: number }>;
  maintenanceTrend: Array<{
    /** Display label for the X-axis: month + year, e.g. "Mar 2026" */
    label: string;
    /** Sortable bucket key in YYYY-MM form. */
    bucket: string;
    completed: number;
    created: number;
  }>;
  riskDistribution: Array<{ name: string; value: number; color: string }>;
}

/** Build a 6-month maintenance trend from the raw task list. Each task
 *  contributes to its `created_at` month (always) and to its `completed_date`
 *  month (if completed within the window). Months with no activity still
 *  appear so the chart has a stable left-to-right span. */
function buildMaintenanceTrend(
  tasks: MaintenanceTask[],
): DashboardData["maintenanceTrend"] {
  const MONTHS = 6;
  const now = new Date();
  const buckets: DashboardData["maintenanceTrend"] = [];
  const indexByKey = new Map<string, number>();

  for (let i = MONTHS - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleDateString(undefined, {
      month: "short",
      year: "numeric",
    });
    indexByKey.set(key, buckets.length);
    buckets.push({ label, bucket: key, completed: 0, created: 0 });
  }

  for (const t of tasks) {
    if (t.created_at) {
      const c = new Date(t.created_at);
      const key = `${c.getFullYear()}-${String(c.getMonth() + 1).padStart(2, "0")}`;
      const idx = indexByKey.get(key);
      if (idx !== undefined) buckets[idx].created += 1;
    }
    if (t.completed_date) {
      const c = new Date(t.completed_date);
      const key = `${c.getFullYear()}-${String(c.getMonth() + 1).padStart(2, "0")}`;
      const idx = indexByKey.get(key);
      if (idx !== undefined) buckets[idx].completed += 1;
    }
  }
  return buckets;
}

const CHART_COLORS = ["#6366f1", "#a855f7", "#22c55e", "#eab308", "#ef4444"];

// Visual mapping for the DMS overview card's per-type breakdown. Matches
// the icons used inside the DMS file list so the dashboard reads consistently
// with the standalone /dms page.
const DOC_TYPE_ICON: Record<DMSDocumentType, typeof FileText> = {
  pdf: FileText,
  image: ImageIcon,
  csv: FileSpreadsheet,
  xlsx: FileSpreadsheet,
  json: FileCode2,
  xml: FileCode2,
  text: FileText,
  docx: FileText,
  other: FileGenericIcon,
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0 },
};

export default function Dashboard() {
  const { selectedOrgId, selectedSiteId } = useAppStore();
  const { needsSiteSelection } = useSiteSelection();
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function loadDashboard() {
      if (!selectedOrgId || needsSiteSelection) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const params: Record<string, string> = {};
        if (selectedSiteId) params.virtual_site_id = selectedSiteId;

        // Maintenance is only fetchable when a site is scoped (the endpoint
        // requires virtual_site_id OR asset_id). We fetch every task in the
        // site so the trend chart works on real history rather than the
        // dashboard's old mock array.
        const maintenancePromise: Promise<MaintenanceTask[]> = selectedSiteId
          ? fetchAllSiteMaintenanceTasks(selectedSiteId)
          : Promise.resolve([]);

        // Instrumentation is only fetchable when a site is scoped (devices
        // endpoint requires virtual_site_id). On the org-wide dashboard view
        // we skip these and the cards show 0.
        const devicesPromise = selectedSiteId
          ? listInstrumentationDevices({
              virtual_site_id: selectedSiteId, page_size: 10,
            })
          : Promise.resolve({ items: [], total: 0, page: 1, page_size: 10, total_pages: 0 });
        const sensorsPromise = selectedSiteId
          ? listInstrumentationSensors({
              virtual_site_id: selectedSiteId, page_size: 200,
            })
          : Promise.resolve({ items: [], total: 0, page: 1, page_size: 1, total_pages: 0 });

        // DMS overview: total docs (across the whole site) + per-type counts.
        // Pull a generous page so the type-breakdown reflects most of the site;
        // truly massive sites would need a backend-side aggregation endpoint,
        // but for the dashboard 500 rows is enough to show the mix.
        const dmsDocsPromise = selectedSiteId
          ? listDMSDocuments({
              virtual_site_id: selectedSiteId,
              include_subfolders: true,
              limit: 500,
            })
          : Promise.resolve({ items: [], total: 0 });
        const dmsFoldersPromise = selectedSiteId
          ? listDMSFolders(selectedSiteId)
          : Promise.resolve([]);

        const [assetsRes, maintenanceRes, locationsRes, pointCloudsRes, digitalTwinsRes, riskRes, devicesRes, sensorsRes, dmsDocsRes, dmsFoldersRes] =
          await Promise.allSettled([
            api.get(`/assets`, { params: { ...params, page_size: 10 } }),
            maintenancePromise,
            api.get(`/functional-locations`, { params: { ...params, page_size: 1 } }),
            api.get(`/point-clouds`, { params: { ...params, page_size: 1 } }),
            api.get(`/digital-twins`, { params: { ...params, page_size: 1 } }),
            api.get(`/risk/assessments`, { params: { ...params, page_size: 5 } }),
            devicesPromise,
            sensorsPromise,
            dmsDocsPromise,
            dmsFoldersPromise,
          ]);

        const assets = assetsRes.status === "fulfilled" ? assetsRes.value.data : { items: [], total: 0 };
        const maintenanceTasks: MaintenanceTask[] =
          maintenanceRes.status === "fulfilled" ? maintenanceRes.value : [];
        const locations = locationsRes.status === "fulfilled" ? locationsRes.value.data : { total: 0 };
        const pointClouds = pointCloudsRes.status === "fulfilled" ? pointCloudsRes.value.data : { total: 0 };
        const digitalTwins = digitalTwinsRes.status === "fulfilled" ? digitalTwinsRes.value.data : { total: 0 };
        const risk = riskRes.status === "fulfilled" ? riskRes.value.data : { items: [], total: 0 };
        const devices = devicesRes.status === "fulfilled" ? devicesRes.value : { items: [], total: 0 };
        const sensors = sensorsRes.status === "fulfilled" ? sensorsRes.value : { items: [], total: 0 };
        const dmsDocs = dmsDocsRes.status === "fulfilled" ? dmsDocsRes.value : { items: [], total: 0 };
        const dmsFolders = dmsFoldersRes.status === "fulfilled" ? dmsFoldersRes.value : [];
        // Tally by document_type so the dashboard card can show a small
        // breakdown (PDF / image / xlsx / etc.) alongside the headline total.
        const typeCounts = new Map<DMSDocumentType, number>();
        for (const d of dmsDocs.items) {
          typeCounts.set(d.document_type, (typeCounts.get(d.document_type) ?? 0) + 1);
        }
        const documentTypeBreakdown = Array.from(typeCounts.entries())
          .map(([type, count]) => ({ type, count }))
          .sort((a, b) => b.count - a.count);
        // Active = sensors whose status is "active" (the default). The
        // /sensors endpoint caps at page_size=200, so on big sites this is a
        // sample of the most-recent; good enough for a dashboard subtitle.
        const activeSensors = (sensors.items || []).filter(
          (s: { status?: string }) => (s.status ?? "active") === "active",
        ).length;

        const todayMidnight = new Date();
        todayMidnight.setHours(0, 0, 0, 0);
        const overdue = maintenanceTasks.filter((t) => {
          if (t.status === "completed" || t.status === "cancelled") return false;
          if (t.status === "overdue") return true;
          if (t.status === "scheduled" && t.scheduled_date) {
            return new Date(t.scheduled_date) < todayMidnight;
          }
          return false;
        }).length;

        const pending = maintenanceTasks.filter(
          (t) => t.status === "scheduled" || t.status === "in_progress",
        ).length;

        const riskScores = (risk.items || [])
          .map((r: { risk_score?: number }) => r.risk_score)
          .filter((s: number | undefined): s is number => s !== undefined);
        const avgRisk =
          riskScores.length > 0
            ? Math.round(riskScores.reduce((a: number, b: number) => a + b, 0) / riskScores.length)
            : 0;

        const highRisk = (risk.items || []).filter(
          (r: { risk_score?: number }) => r.risk_score !== undefined && r.risk_score >= 15
        ).length;

        setData({
          totalAssets: assets.total || 0,
          totalLocations: locations.total || 0,
          totalPointClouds: pointClouds.total || 0,
          totalDigitalTwins: digitalTwins.total || 0,
          totalDevices: devices.total || 0,
          totalSensors: sensors.total || 0,
          activeSensors,
          totalDocuments: dmsDocs.total || 0,
          totalFolders: dmsFolders.length,
          documentTypeBreakdown,
          overdueMaintenance: overdue,
          pendingMaintenance: pending,
          averageRiskScore: avgRisk,
          highRiskCount: highRisk,
          // The devices list endpoint sorts by created_at desc, so the first
          // 5 are also the most recent. Map to the shape the UI needs.
          recentDevices: (devices.items || []).slice(0, 5).map((d) => ({
            id: d.id,
            name: d.name,
            status: d.status,
            device_type: d.device_type,
            serial_number: d.serial_number,
            sensor_count: d.sensor_count ?? 0,
            updated_at: d.updated_at,
          })),
          recentAssets: (assets.items || []).slice(0, 10),
          // The list endpoint already sorts maintenance tasks by ascending
          // scheduled_date, so "most recent" here means most-recently-created;
          // sort the in-memory copy by created_at to surface the latest first.
          recentMaintenance: [...maintenanceTasks]
            .sort((a, b) => b.created_at.localeCompare(a.created_at))
            .slice(0, 10)
            .map((t) => ({
              id: t.id,
              description: t.description,
              status: t.status,
              scheduled_date: t.scheduled_date,
            })),
          assetsByStatus: [
            { name: "Active", value: Math.max(1, Math.floor((assets.total || 1) * 0.7)) },
            { name: "Maintenance", value: Math.max(0, Math.floor((assets.total || 0) * 0.15)) },
            { name: "Offline", value: Math.max(0, Math.floor((assets.total || 0) * 0.15)) },
          ],
          maintenanceTrend: buildMaintenanceTrend(maintenanceTasks),
          riskDistribution: [
            { name: "Low", value: Math.max(1, (risk.total || 1) - highRisk), color: "#22c55e" },
            { name: "Medium", value: Math.max(0, Math.floor(highRisk * 0.5)), color: "#eab308" },
            { name: "High", value: Math.max(0, highRisk), color: "#ef4444" },
          ],
        });
      } catch {
        setData({
          totalAssets: 0,
          totalLocations: 0,
          totalPointClouds: 0,
          totalDigitalTwins: 0,
          totalDevices: 0,
          totalSensors: 0,
          activeSensors: 0,
          totalDocuments: 0,
          totalFolders: 0,
          documentTypeBreakdown: [],
          overdueMaintenance: 0,
          pendingMaintenance: 0,
          averageRiskScore: 0,
          highRiskCount: 0,
          recentDevices: [],
          recentAssets: [],
          recentMaintenance: [],
          assetsByStatus: [],
          maintenanceTrend: [],
          riskDistribution: [],
        });
      } finally {
        setIsLoading(false);
      }
    }

    loadDashboard();
  }, [selectedOrgId, selectedSiteId, needsSiteSelection]);

  if (needsSiteSelection) {
    return (
      <div className="flex h-96 flex-col items-center justify-center gap-4 text-surface-400">
        <div className="relative">
          <MapPin className="h-12 w-12 text-amber-400" />
          <span className="absolute inset-0 rounded-full bg-amber-500/20 animate-ping" />
        </div>
        <p className="text-lg text-surface-200 font-medium">
          Select a virtual site to continue
        </p>
        <p className="max-w-md text-center text-sm text-surface-400">
          Use the selector at the top of the page to pick the site you want to
          work in. The dashboard and navigation unlock once a site is chosen.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <div className="h-8 w-48 rounded-lg bg-surface-700/50 animate-pulse" />
          <div className="h-4 w-72 rounded-lg bg-surface-700/30 animate-pulse mt-2" />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      </div>
    );
  }

  if (!selectedOrgId) {
    return (
      <div className="flex h-96 flex-col items-center justify-center gap-4 text-surface-400">
        <MapPin className="h-12 w-12 text-surface-600" />
        <p className="text-lg">Select an organization to view the dashboard</p>
      </div>
    );
  }

  if (!data) return null;

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      <motion.div variants={itemVariants}>
        <h1 className="text-2xl font-bold text-surface-100">Dashboard</h1>
        <p className="text-sm text-surface-400 mt-1">
          Overview of your asset management ecosystem
        </p>
      </motion.div>

      {/* KPI Cards */}
      <motion.div
        variants={itemVariants}
        className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4"
      >
        <KPICard
          title="Total Assets"
          value={data.totalAssets}
          icon={<Box className="h-5 w-5" />}
          color="brand"
          subtitle={`${data.totalLocations} locations`}
          onClick={() => navigate("/assets")}
        />
        <KPICard
          title="Overdue Tasks"
          value={data.overdueMaintenance}
          icon={<AlertTriangle className="h-5 w-5" />}
          color={data.overdueMaintenance > 0 ? "red" : "emerald"}
          subtitle={`${data.pendingMaintenance} pending`}
          onClick={() => navigate("/maintenance")}
        />
        <KPICard
          title="Avg Risk Score"
          value={data.averageRiskScore}
          icon={<ShieldAlert className="h-5 w-5" />}
          color={data.averageRiskScore >= 15 ? "red" : data.averageRiskScore >= 8 ? "amber" : "emerald"}
          subtitle={`${data.highRiskCount} high risk`}
          badge="WIP"
          onClick={() => navigate("/risk")}
        />
        <KPICard
          title="Point Clouds"
          value={data.totalPointClouds}
          icon={<Activity className="h-5 w-5" />}
          color="blue"
          subtitle="3D scans"
          onClick={() => navigate("/point-clouds")}
        />
        <KPICard
          title="Digital Twins"
          value={data.totalDigitalTwins}
          icon={<Layers className="h-5 w-5" />}
          color="purple"
          subtitle="3D Gaussian splats"
          onClick={() => navigate("/digital-twins")}
        />
        <KPICard
          title="Devices"
          value={data.totalDevices}
          icon={<Cpu className="h-5 w-5" />}
          color="emerald"
          subtitle={`${data.totalSensors} sensors`}
          onClick={() => navigate("/instrumentation")}
        />
        <KPICard
          title="Active Sensors"
          value={data.activeSensors}
          icon={<Radio className="h-5 w-5" />}
          color={data.activeSensors > 0 ? "blue" : "amber"}
          subtitle={
            data.totalSensors > 0
              ? `${data.activeSensors} of ${data.totalSensors} streaming`
              : "No sensors yet"
          }
          onClick={() => navigate("/instrumentation")}
        />
        <KPICard
          title="Documents"
          value={data.totalDocuments}
          icon={<FolderTree className="h-5 w-5" />}
          color="purple"
          subtitle={
            data.totalFolders > 0
              ? `${data.totalFolders} folder${data.totalFolders === 1 ? "" : "s"}`
              : "No folders yet"
          }
          onClick={() => navigate("/dms")}
        />
      </motion.div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Maintenance Trend */}
        <motion.div variants={itemVariants} className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Maintenance Trend</CardTitle>
            </CardHeader>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.maintenanceTrend}>
                  <defs>
                    <linearGradient id="colorCompleted" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorCreated" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#a855f7" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#a855f7" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="label" stroke="#64748b" fontSize={12} />
                  <YAxis stroke="#64748b" fontSize={12} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#1e293b",
                      border: "1px solid #334155",
                      borderRadius: "8px",
                      color: "#f1f5f9",
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="completed"
                    stroke="#6366f1"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorCompleted)"
                    name="Completed"
                    dot={{ r: 4, fill: "#6366f1", stroke: "#0f172a", strokeWidth: 2 }}
                    activeDot={{ r: 6, fill: "#6366f1", stroke: "#f1f5f9", strokeWidth: 2 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="created"
                    stroke="#a855f7"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorCreated)"
                    name="Created"
                    dot={{ r: 4, fill: "#a855f7", stroke: "#0f172a", strokeWidth: 2 }}
                    activeDot={{ r: 6, fill: "#a855f7", stroke: "#f1f5f9", strokeWidth: 2 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </motion.div>

        {/* Risk Distribution */}
        <motion.div variants={itemVariants}>
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <CardTitle>Risk Distribution</CardTitle>
                <span className="inline-flex items-center rounded-full border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-300">
                  WIP
                </span>
              </div>
            </CardHeader>
            <div className="h-64 flex items-center justify-center gap-4">
              {data.riskDistribution.some((d) => d.value > 0) ? (
                <>
                  <div className="flex-1 h-full min-w-0">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={data.riskDistribution}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={5}
                          dataKey="value"
                        >
                          {data.riskDistribution.map((entry, index) => (
                            <Cell key={index} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "#1e293b",
                            border: "1px solid #334155",
                            borderRadius: "8px",
                            color: "#f1f5f9",
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex flex-col gap-2 pr-2">
                    {data.riskDistribution.map((item) => (
                      <div key={item.name} className="flex items-center gap-2 text-xs">
                        <div
                          className="h-2.5 w-2.5 rounded-full"
                          style={{ backgroundColor: item.color }}
                        />
                        <span className="text-surface-400">
                          {item.name}: {item.value}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-sm text-surface-500">No risk data</p>
              )}
            </div>
          </Card>
        </motion.div>
      </div>

      {/* Assets by Status + Recent Lists */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Asset Status Bar Chart */}
        <motion.div variants={itemVariants} className="h-48">
          <Card className="h-full flex flex-col">
            <CardHeader>
              <CardTitle>Assets by Status</CardTitle>
            </CardHeader>
            <div className="flex-1 min-h-0">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.assetsByStatus}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="name" stroke="#64748b" fontSize={12} />
                  <YAxis stroke="#64748b" fontSize={12} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#1e293b",
                      border: "1px solid #334155",
                      borderRadius: "8px",
                      color: "#f1f5f9",
                    }}
                  />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                    {data.assetsByStatus.map((_, index) => (
                      <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </motion.div>

        {/* Recent Assets */}
        <motion.div variants={itemVariants} className="h-48">
          <Card className="h-full flex flex-col">
            <CardHeader>
              <CardTitle>Recent Assets</CardTitle>
            </CardHeader>
            <div className="flex-1 min-h-0 overflow-y-scroll pr-1 space-y-3">
              {data.recentAssets.length === 0 ? (
                <p className="text-sm text-surface-500 text-center py-4">
                  No assets yet
                </p>
              ) : (
                data.recentAssets.map((asset) => (
                  <button
                    key={asset.id}
                    onClick={() => navigate(`/assets/${asset.id}`)}
                    className="flex w-full items-center justify-between rounded-lg p-2 hover:bg-surface-700/50 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-500/15">
                        <Box className="h-4 w-4 text-brand-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-surface-200">
                          {asset.name}
                        </p>
                        <p className="text-xs text-surface-500">
                          {formatDate(asset.created_at)}
                        </p>
                      </div>
                    </div>
                    <StatusBadge status={asset.status || "active"} />
                  </button>
                ))
              )}
            </div>
          </Card>
        </motion.div>

        {/* Recent Maintenance */}
        <motion.div variants={itemVariants} className="h-48">
          <Card className="h-full flex flex-col">
            <CardHeader>
              <CardTitle>Recent Tasks</CardTitle>
            </CardHeader>
            <div className="flex-1 min-h-0 overflow-y-scroll pr-1 space-y-3">
              {data.recentMaintenance.length === 0 ? (
                <p className="text-sm text-surface-500 text-center py-4">
                  No maintenance tasks
                </p>
              ) : (
                data.recentMaintenance.map((task) => (
                  <button
                    key={task.id}
                    onClick={() => navigate("/maintenance")}
                    className="flex w-full items-center justify-between rounded-lg p-2 hover:bg-surface-700/50 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-500/15">
                        <Wrench className="h-4 w-4 text-amber-400" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-surface-200 line-clamp-1">
                          {task.description}
                        </p>
                        {task.scheduled_date && (
                          <div className="flex items-center gap-1 text-xs text-surface-500">
                            <Clock className="h-3 w-3" />
                            {formatDate(task.scheduled_date)}
                          </div>
                        )}
                      </div>
                    </div>
                    <StatusBadge status={task.status || "pending"} />
                  </button>
                ))
              )}
            </div>
          </Card>
        </motion.div>
      </div>

      {/* Document Management overview */}
      <motion.div variants={itemVariants}>
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                <FolderTree className="h-4 w-4 text-brand-300" />
                <CardTitle>Documents</CardTitle>
              </div>
              <button
                type="button"
                onClick={() => navigate("/dms")}
                className="text-xs text-brand-300 hover:text-brand-200"
              >
                Open DMS →
              </button>
            </div>
          </CardHeader>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-[180px_1fr]">
            {/* Totals at a glance */}
            <div className="flex flex-col gap-3">
              <div className="rounded-lg border border-surface-700/50 bg-surface-900/40 px-3 py-2.5">
                <div className="text-[10px] uppercase tracking-wider text-surface-500">
                  Total files
                </div>
                <div className="mt-0.5 text-2xl font-semibold text-surface-100">
                  {data.totalDocuments}
                </div>
              </div>
              <div className="rounded-lg border border-surface-700/50 bg-surface-900/40 px-3 py-2.5">
                <div className="text-[10px] uppercase tracking-wider text-surface-500">
                  Folders
                </div>
                <div className="mt-0.5 text-2xl font-semibold text-surface-100">
                  {data.totalFolders}
                </div>
              </div>
            </div>
            {/* Per-type breakdown */}
            <div>
              <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-surface-500">
                By type
              </div>
              {data.documentTypeBreakdown.length === 0 ? (
                <p className="text-sm text-surface-500">
                  No documents in the DMS yet. Upload one via the Documents page or the
                  detail page of any asset, device, sensor, or maintenance task.
                </p>
              ) : (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                  {data.documentTypeBreakdown.map((item) => {
                    const Icon = DOC_TYPE_ICON[item.type] ?? FileGenericIcon;
                    return (
                      <button
                        key={item.type}
                        type="button"
                        onClick={() => navigate("/dms")}
                        className="flex items-center gap-2 rounded-lg border border-surface-700/50 bg-surface-900/40 px-3 py-2 text-left transition-colors hover:border-brand-500/40 hover:bg-brand-500/5"
                      >
                        <Icon className="h-4 w-4 shrink-0 text-brand-300" />
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-medium text-surface-100">
                            {item.count}
                          </div>
                          <div className="text-[11px] uppercase tracking-wider text-surface-500">
                            {item.type}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
              {data.totalDocuments > data.documentTypeBreakdown.reduce((a, b) => a + b.count, 0) && (
                <p className="mt-2 text-[11px] text-surface-500">
                  Showing the most recent {data.documentTypeBreakdown.reduce((a, b) => a + b.count, 0)} of {data.totalDocuments} files for the type breakdown.
                </p>
              )}
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Recent Devices (Instrumentation) */}
      <motion.div variants={itemVariants}>
        <Card>
          <CardHeader>
            <CardTitle>Recent Devices</CardTitle>
          </CardHeader>
          {data.recentDevices.length === 0 ? (
            <p className="text-sm text-surface-500 text-center py-6">
              No instrumentation devices yet.
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
              {data.recentDevices.map((device) => (
                <button
                  key={device.id}
                  onClick={() => navigate("/instrumentation")}
                  className="flex items-center gap-3 rounded-lg border border-surface-700/50 bg-surface-900/40 p-3 text-left hover:bg-surface-800/40 transition-colors"
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-500/15">
                    <Cpu className="h-4 w-4 text-emerald-400" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-surface-100">
                      {device.name}
                    </p>
                    <p className="truncate text-xs text-surface-500">
                      {device.device_type ?? "device"}
                      {device.serial_number && (
                        <> · <span className="font-mono">{device.serial_number}</span></>
                      )}
                    </p>
                    <p className="text-[11px] text-surface-500">
                      <Radio className="inline h-3 w-3 mr-1 text-cyan-300" />
                      {device.sensor_count} sensor{device.sensor_count === 1 ? "" : "s"}
                    </p>
                  </div>
                  <StatusBadge status={device.status} />
                </button>
              ))}
            </div>
          )}
        </Card>
      </motion.div>

      {/* Quick Stats Footer */}
      <motion.div variants={itemVariants}>
        <Card variant="glass" padding="sm">
          <div className="flex flex-wrap items-center justify-center gap-6 py-2">
            <div className="flex items-center gap-2 text-sm">
              <span className="relative flex h-2 w-2">
                <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-50" />
                <span className="relative h-2 w-2 rounded-full bg-emerald-400" />
              </span>
              <span className="text-surface-400">System Operational</span>
            </div>
            <div className="h-4 w-px bg-surface-700" />
            <div className="text-sm text-surface-400">
              <span className="font-medium text-surface-200">{data.totalAssets}</span> assets tracked
            </div>
            <div className="h-4 w-px bg-surface-700" />
            <div className="text-sm text-surface-400">
              <span className="font-medium text-surface-200">{data.totalLocations}</span> locations
            </div>
            <div className="h-4 w-px bg-surface-700" />
            <div className="text-sm text-surface-400">
              <span className="font-medium text-surface-200">{data.totalDevices}</span> devices ·{" "}
              <span className="font-medium text-surface-200">{data.totalSensors}</span> sensors
            </div>
          </div>
        </Card>
      </motion.div>
    </motion.div>
  );
}
